// =============================================================================
// ResourceEditor.jsx — TipTap WYSIWYG for Resources content pages
// =============================================================================
// The Resources content editor (Manage → Resource Pages). Uses TipTap
// (ProseMirror) — chosen over Quill after evaluation (John, 25 Jul 2026). ONLY
// the Resources area uses TipTap; the CRM email composer stays on react-quill-new
// (its email-safe inline-style handling is left untouched).
//
// Same public contract as before — { value, onChange } where onChange receives
// the body HTML — so ResourcePagesAdmin, the sanitiser (services/resourceHtml),
// and the read-only ResourceSection are all unchanged. Content is portable HTML.
// =============================================================================

import { Extension } from "@tiptap/core";
import Color from "@tiptap/extension-color";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import React from "react";
import { getResourceDocumentById } from "../../data/resourceDocuments";
import { uploadResourceImage } from "../../data/storage";
import { alertDialog } from "../../services/dialogService";
import { HELP_CATALOG, HELP_TABS, helpLabel, helpTabOf } from "../../services/helpCatalog";
import { parseResourceDocId } from "../../services/resourceDocs";
import { helpHref } from "../../services/resourceHelpLinks";
import { signResourceImagesInHtml } from "../../services/resourceImageHtml";
import { resourceImageError } from "../../services/resourceImages";
import { ModalBackdrop } from "../form/FormUI.jsx";

// -----------------------------------------------------------------------------
// Block indent — StarterKit has no paragraph/heading indent (the ⇤ ↔ ⇥ buttons are
// text-align). This adds an `indent` level (0..MAX) to paragraphs + headings,
// rendered as an inline `margin-left` (survives the sanitiser + renders identically
// in the read view). Inside a list, indent/outdent uses ProseMirror's native
// sink/lift so nesting behaves as expected.
// -----------------------------------------------------------------------------
const INDENT_TYPES = ["paragraph", "heading"];
const INDENT_STEP_EM = 2; // em per indent level
const INDENT_MAX = 10;

const Indent = Extension.create({
  name: "indent",
  addGlobalAttributes() {
    return [
      {
        types: INDENT_TYPES,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (el) => {
              const ml = Number.parseFloat(el.style?.marginLeft || "0");
              return Number.isFinite(ml) && ml > 0
                ? Math.min(Math.round(ml / INDENT_STEP_EM), INDENT_MAX)
                : 0;
            },
            renderHTML: (attrs) => {
              const lvl = attrs.indent || 0;
              return lvl > 0 ? { style: `margin-left:${lvl * INDENT_STEP_EM}em` } : {};
            },
          },
        },
      },
    ];
  },
  addCommands() {
    const shift =
      (delta) =>
      ({ editor, state, tr, dispatch }) => {
        // Lists: use native nesting so bullets/numbers re-level correctly.
        if (editor.isActive("listItem")) {
          return delta > 0
            ? editor.commands.sinkListItem("listItem")
            : editor.commands.liftListItem("listItem");
        }
        const { from, to } = state.selection;
        let changed = false;
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (!INDENT_TYPES.includes(node.type.name)) return;
          const cur = node.attrs.indent || 0;
          const next = Math.max(0, Math.min(INDENT_MAX, cur + delta));
          if (next !== cur) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
            changed = true;
          }
        });
        if (changed && dispatch) dispatch(tr);
        return changed;
      };
    return { indent: () => shift(1), outdent: () => shift(-1) };
  },
});

// -----------------------------------------------------------------------------
// Image wrap — extends the base Image node with an `align` attribute so an image
// can float left/right (text wraps beside it, ≈ Word "Square"/"Tight"), sit
// centred, or stay inline (default, no wrap). Rendered as an inline style +
// data-align so it round-trips through save/sanitise and renders self-contained in
// the read view (no extra CSS needed there). Behind/In-Front/free-drag aren't
// representable in flowing HTML, so they're intentionally not offered.
// -----------------------------------------------------------------------------
const IMAGE_ALIGN_STYLE = {
  left: "float:left;margin:4px 16px 8px 0;",
  right: "float:right;margin:4px 0 8px 16px;",
  center: "display:block;margin:8px auto;",
};

const ResourceImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-align") || null,
        renderHTML: (attrs) => {
          const style = IMAGE_ALIGN_STYLE[attrs.align];
          return style ? { "data-align": attrs.align, style } : {};
        },
      },
    };
  },
});

const EDITOR_CSS = `
.resource-tiptap .tiptap-toolbar{display:flex;flex-wrap:wrap;gap:4px;padding:6px;border:1px solid #CFD8DC;border-bottom:none;border-radius:8px 8px 0 0;background:#F7F9F9}
.resource-tiptap .tiptap-toolbar button{min-width:30px;height:30px;padding:0 8px;border:1px solid transparent;border-radius:6px;background:transparent;color:#1A2E2B;font-size:13px;cursor:pointer;font-family:inherit}
.resource-tiptap .tiptap-toolbar button:hover{background:#E3EBEA}
.resource-tiptap .tiptap-toolbar button.is-active{background:#00695C;color:#fff}
.resource-tiptap .tiptap-toolbar .sep{width:1px;background:#CFD8DC;margin:2px 4px}
.resource-tiptap .ProseMirror{min-height:320px;border:1px solid #CFD8DC;border-radius:0 0 8px 8px;padding:12px 14px;font-size:14px;line-height:1.6;color:#1A2E2B;outline:none;background:#fff}
.resource-tiptap .ProseMirror:focus{border-color:#00897B}
.resource-tiptap .ProseMirror p{margin:0 0 .7em}
.resource-tiptap .ProseMirror h1{font-size:1.6em;font-weight:700;margin:1em 0 .3em}
.resource-tiptap .ProseMirror h2{font-size:1.35em;font-weight:700;margin:1em 0 .3em}
.resource-tiptap .ProseMirror h3{font-size:1.15em;font-weight:700;margin:1em 0 .3em}
.resource-tiptap .ProseMirror>:first-child{margin-top:0}
.resource-tiptap .ProseMirror ul{list-style:disc;padding-left:1.4em}
.resource-tiptap .ProseMirror ol{list-style:decimal;padding-left:1.4em}
.resource-tiptap .ProseMirror blockquote{border-left:3px solid #CFE0DE;margin:.4em 0;padding-left:12px;color:#4a5c59}
.resource-tiptap .ProseMirror a{color:#00695C;text-decoration:underline}
.resource-tiptap .ProseMirror pre{background:#1A2E2B;color:#E0F2F1;padding:10px 12px;border-radius:6px;overflow-x:auto;font-family:ui-monospace,Menlo,Consolas,monospace;font-size:13px}
.resource-tiptap .ProseMirror code{font-family:ui-monospace,Menlo,Consolas,monospace}
.resource-tiptap .ProseMirror img{max-width:100%;height:auto;border-radius:4px}
.resource-tiptap .ProseMirror table{border-collapse:collapse;margin:8px 0;overflow:hidden}
.resource-tiptap .ProseMirror td,.resource-tiptap .ProseMirror th{border:1px solid #CFD8DC;padding:5px 9px;min-width:44px;vertical-align:top}
.resource-tiptap .ProseMirror th{background:#EEF4F3;font-weight:700;text-align:left}
.resource-tiptap .ProseMirror .selectedCell{background:#E3EBEA}
.resource-tiptap .ProseMirror::after{content:"";display:block;clear:both}
`;

function Btn({ onClick, active, title, children, disabled }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={active ? "is-active" : ""}
      style={disabled ? { opacity: 0.4, cursor: "default" } : undefined}
    >
      {children}
    </button>
  );
}

const LINK_LABEL_CLS =
  "text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1";
const LINK_INPUT_CLS =
  "w-full py-[8px] px-[10px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white";

export function ResourceEditor({ value = "", onChange }) {
  // Track the last HTML we emitted, so an external value change (switching pages)
  // reloads the editor, but our own keystroke-driven updates don't reset the
  // cursor. Starts as null (not `value`) so the load effect runs once on mount to
  // SIGN the initial content — resource images are private (db-185) and need a
  // signed URL to display in the editor.
  const lastEmitted = React.useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color,
      Indent,
      ResourceImage.configure({ inline: false }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      lastEmitted.current = html;
      if (onChange) onChange(html);
    },
  });

  React.useEffect(
    function () {
      if (!editor) return;
      if (value === lastEmitted.current) return;
      // Record the ORIGINAL value as emitted, so our own keystroke updates (which
      // emit the signed HTML back through onChange) don't re-trigger this.
      lastEmitted.current = value;
      let live = true;
      // Sign the inline resource-image srcs before loading, so the author sees the
      // images (the bucket is private — db-185). `false` = don't emit an update
      // (avoids a feedback loop). Signing is display-only and re-derivable from the
      // stored key, so persisting the signed HTML is harmless (re-signed on render).
      signResourceImagesInHtml(value || "").then(function (signedHtml) {
        if (live && editor) editor.commands.setContent(signedHtml || "", false);
      });
      return function () {
        live = false;
      };
    },
    [value, editor]
  );

  const fileInputRef = React.useRef(null);
  async function onPickImage(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file || !editor) return;
    // Guard the upload: 5 MB limit + MIME whitelist, SVG blocked (db-185). The
    // bucket enforces the same, but this fails fast with a readable message.
    const rejectReason = resourceImageError(file);
    if (rejectReason) {
      await alertDialog({ title: "Image not allowed", message: rejectReason });
      return;
    }
    const rawExt = (file.name.split(".").pop() || "png").toLowerCase().replace(/[^a-z0-9]/g, "");
    const path = `${crypto.randomUUID()}.${rawExt || "png"}`;
    const { error, url } = await uploadResourceImage(path, file);
    if (error || !url) {
      await alertDialog({ title: "Upload failed", message: error?.message || "unknown error" });
      return;
    }
    editor.chain().focus().setImage({ src: url }).run();
  }

  // ── Link dialog ────────────────────────────────────────────────────────────
  // TipTap's link is a MARK, so setLink on a collapsed cursor marks a zero-length
  // range and nothing appears — which is what happens if you just click Link on an
  // empty line. So the dialog takes the link TEXT as well as the URL and inserts
  // both when there's no selection.
  //
  // Paste a Resources document link and the document's NAME is offered as the text
  // automatically (that's what the display name is for).
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState("");
  const [linkText, setLinkText] = React.useState("");
  const [linkLookup, setLinkLookup] = React.useState(false);
  // The text as it was when the dialog opened — if the author doesn't change it we
  // mark the existing text in place rather than replacing it (keeps its formatting).
  const originalText = React.useRef("");
  // Guards the name lookup so retyping the same URL doesn't refetch.
  const lookedUpId = React.useRef("");

  function openLink() {
    if (!editor) return;
    const sel = editor.state.selection;
    const selected = sel.empty ? "" : editor.state.doc.textBetween(sel.from, sel.to, " ");
    originalText.current = selected;
    lookedUpId.current = "";
    setLinkUrl(editor.getAttributes("link").href || "");
    setLinkText(selected);
    setLinkOpen(true);
  }

  // When the URL is (or becomes) a document link, fill the text with the document's
  // name — unless the author has already typed their own text.
  async function onLinkUrlChange(url) {
    setLinkUrl(url);
    const id = parseResourceDocId(url);
    if (!id || id === lookedUpId.current) return;
    lookedUpId.current = id;
    setLinkLookup(true);
    const row = await getResourceDocumentById(id);
    setLinkLookup(false);
    if (!row?.name) return;
    setLinkText(function (cur) {
      return cur && cur !== originalText.current ? cur : row.name;
    });
  }

  function closeLink() {
    setLinkOpen(false);
    setLinkLookup(false);
  }

  // Pick a Help item → the link becomes a `#help=<key>` deep link that opens the
  // shared help modal on click (never navigates). Defaults the display text to the
  // item's label unless the author already typed their own.
  function onPickHelp(key) {
    if (!key) return;
    setLinkUrl(helpHref(key));
    setLinkText(function (cur) {
      return cur && cur !== originalText.current ? cur : helpLabel(key);
    });
  }

  function removeLink() {
    if (editor) editor.chain().focus().extendMarkRange("link").unsetLink().run();
    closeLink();
  }

  function applyLink() {
    if (!editor) return;
    const href = linkUrl.trim();
    const text = linkText.trim();
    if (!href) {
      removeLink();
      return;
    }
    const chain = editor.chain().focus();
    const onExistingLink = editor.isActive("link");
    const hasRange = !editor.state.selection.empty || onExistingLink;
    // A bare cursor inside a link: widen to the whole link so we edit all of it.
    if (onExistingLink) chain.extendMarkRange("link");
    if (hasRange && (!text || text === originalText.current)) {
      // Existing text kept as-is — just (re)point it at the URL.
      chain.setLink({ href });
    } else {
      // No selection, or the author gave different text: write the text out and
      // link it. unsetMark stops the link continuing as they keep typing.
      chain
        .insertContent({
          type: "text",
          text: text || href,
          marks: [{ type: "link", attrs: { href } }],
        })
        .unsetMark("link");
    }
    chain.run();
    closeLink();
  }

  // Set the wrap/alignment of the currently-selected image. `inline` clears the
  // attribute (default: no wrap, image on its own line). No-op if no image is
  // selected — the buttons are disabled in that case anyway.
  function setImgAlign(align) {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .updateAttributes("image", { align: align === "inline" ? null : align })
      .run();
  }

  return (
    <div className="resource-tiptap">
      <style>{EDITOR_CSS}</style>
      {editor && (
        <div className="tiptap-toolbar">
          <Btn
            title="Heading 1"
            active={editor.isActive("heading", { level: 1 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          >
            H1
          </Btn>
          <Btn
            title="Heading 2"
            active={editor.isActive("heading", { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            H2
          </Btn>
          <Btn
            title="Heading 3"
            active={editor.isActive("heading", { level: 3 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            H3
          </Btn>
          <span className="sep" />
          <Btn
            title="Bold"
            active={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <strong>B</strong>
          </Btn>
          <Btn
            title="Italic"
            active={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <em>I</em>
          </Btn>
          <Btn
            title="Underline"
            active={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <span style={{ textDecoration: "underline" }}>U</span>
          </Btn>
          <Btn
            title="Strikethrough"
            active={editor.isActive("strike")}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <span style={{ textDecoration: "line-through" }}>S</span>
          </Btn>
          <label
            title="Text colour"
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: 30,
              padding: "0 4px",
              cursor: "pointer",
            }}
          >
            <input
              type="color"
              aria-label="Text colour"
              onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
              style={{ width: 22, height: 22, border: "none", background: "none", padding: 0 }}
            />
          </label>
          <span className="sep" />
          <Btn
            title="Bullet list"
            active={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            • List
          </Btn>
          <Btn
            title="Numbered list"
            active={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            1. List
          </Btn>
          <Btn
            title="Quote"
            active={editor.isActive("blockquote")}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            “ ”
          </Btn>
          <Btn
            title="Code block"
            active={editor.isActive("codeBlock")}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          >
            {"</>"}
          </Btn>
          <span className="sep" />
          <Btn
            title="Align left"
            active={editor.isActive({ textAlign: "left" })}
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
          >
            ⇤
          </Btn>
          <Btn
            title="Align centre"
            active={editor.isActive({ textAlign: "center" })}
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
          >
            ↔
          </Btn>
          <Btn
            title="Align right"
            active={editor.isActive({ textAlign: "right" })}
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
          >
            ⇥
          </Btn>
          <Btn title="Decrease indent" onClick={() => editor.chain().focus().outdent().run()}>
            ◄▏
          </Btn>
          <Btn title="Increase indent" onClick={() => editor.chain().focus().indent().run()}>
            ▏►
          </Btn>
          <span className="sep" />
          <Btn title="Link" active={editor.isActive("link")} onClick={openLink}>
            🔗
          </Btn>
          <Btn
            title="Clear formatting"
            onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          >
            ⌫
          </Btn>
          <span className="sep" />
          <Btn
            title="Insert image"
            onClick={() => {
              if (fileInputRef.current) fileInputRef.current.click();
            }}
          >
            🖼
          </Btn>
          <Btn
            title="Wrap image left (text on the right)"
            disabled={!editor.isActive("image")}
            active={editor.isActive("image", { align: "left" })}
            onClick={() => setImgAlign("left")}
          >
            🖼◀
          </Btn>
          <Btn
            title="Centre image"
            disabled={!editor.isActive("image")}
            active={editor.isActive("image", { align: "center" })}
            onClick={() => setImgAlign("center")}
          >
            🖼▣
          </Btn>
          <Btn
            title="Wrap image right (text on the left)"
            disabled={!editor.isActive("image")}
            active={editor.isActive("image", { align: "right" })}
            onClick={() => setImgAlign("right")}
          >
            ▶🖼
          </Btn>
          <Btn
            title="Inline image (no wrap)"
            disabled={!editor.isActive("image")}
            active={editor.isActive("image") && !editor.getAttributes("image").align}
            onClick={() => setImgAlign("inline")}
          >
            🖼—
          </Btn>
          <Btn
            title="Insert table"
            onClick={() =>
              editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
            }
          >
            ⊞ Table
          </Btn>
          <Btn title="Add column" onClick={() => editor.chain().focus().addColumnAfter().run()}>
            +Col
          </Btn>
          <Btn title="Add row" onClick={() => editor.chain().focus().addRowAfter().run()}>
            +Row
          </Btn>
          <Btn title="Delete column" onClick={() => editor.chain().focus().deleteColumn().run()}>
            −Col
          </Btn>
          <Btn title="Delete row" onClick={() => editor.chain().focus().deleteRow().run()}>
            −Row
          </Btn>
          <Btn title="Delete table" onClick={() => editor.chain().focus().deleteTable().run()}>
            ⌫Tbl
          </Btn>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        style={{ display: "none" }}
        onChange={onPickImage}
      />
      <EditorContent editor={editor} />

      {linkOpen && (
        <ModalBackdrop
          onClose={closeLink}
          portal
          contentStyle={{
            background: "#FFFFFF",
            borderRadius: 12,
            padding: 24,
            width: "min(560px, calc(100vw - 40px))",
          }}
        >
          <div className="text-[17px] font-bold text-zephyr-teal-dark mb-1">Insert link</div>
          <div className="text-[12px] text-zephyr-muted mb-4">
            Paste a document link from Manage → Resources Management → Documents and the document’s
            name fills in automatically, or pick a Help item below to link to a (?) help bubble.
          </div>

          <label htmlFor="rl-help" className={LINK_LABEL_CLS}>
            Link to a Help item
          </label>
          <select
            id="rl-help"
            value=""
            onChange={(e) => onPickHelp(e.target.value)}
            className={`${LINK_INPUT_CLS} mb-3`}
          >
            <option value="">— none (use a URL below) —</option>
            {HELP_TABS.map((t) => (
              <optgroup key={t.id} label={t.label}>
                {HELP_CATALOG.filter((h) => helpTabOf(h.key) === t.id).map((h) => (
                  <option key={h.key} value={h.key}>
                    {h.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          <label htmlFor="rl-url" className={LINK_LABEL_CLS}>
            Link URL
          </label>
          <input
            id="rl-url"
            type="text"
            value={linkUrl}
            onChange={(e) => onLinkUrlChange(e.target.value)}
            placeholder="e.g. https://portal.zephyreducation.com.au/resource-doc/…"
            className={`${LINK_INPUT_CLS} mb-3`}
          />

          <label htmlFor="rl-text" className={LINK_LABEL_CLS}>
            Text to display
          </label>
          <input
            id="rl-text"
            type="text"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            placeholder={linkLookup ? "Looking up the document…" : "e.g. Uniform Request Form"}
            className={LINK_INPUT_CLS}
          />
          <div className="text-[11px] text-zephyr-muted mt-1 mb-4">
            Leave blank to use the URL itself as the text.
          </div>

          <div className="flex gap-2 items-center">
            <button
              type="button"
              onClick={applyLink}
              disabled={!linkUrl.trim()}
              className="bg-zephyr-teal text-white rounded-[8px] py-[8px] px-5 text-[14px] font-semibold cursor-pointer border-none disabled:opacity-60"
              style={{ fontFamily: "inherit" }}
            >
              Insert link
            </button>
            <button
              type="button"
              onClick={closeLink}
              className="bg-transparent rounded-[8px] py-[8px] px-4 text-[14px] font-semibold cursor-pointer"
              style={{ border: "1px solid #607D79", color: "#607D79", fontFamily: "inherit" }}
            >
              Cancel
            </button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={removeLink}
              className="bg-transparent border-none cursor-pointer text-[13px] text-[#C62828]"
              style={{ fontFamily: "inherit" }}
            >
              Remove link
            </button>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}
